import { Injectable } from '@angular/core'
import { BehaviorSubject } from 'rxjs'

export type UiDialogMode = 'alert' | 'confirm'

export interface UiDialogState {
	mode: UiDialogMode
	title: string
	message: string
	primaryLabel: string
	secondaryLabel?: string
	showCloseButton: boolean
	dismissableMask: boolean
	closeOnEscape: boolean
}

export interface UiAlertOptions {
	title?: string
	primaryLabel?: string
	showCloseButton?: boolean
	dismissableMask?: boolean
	closeOnEscape?: boolean
}

export interface UiConfirmOptions extends UiAlertOptions {
	secondaryLabel?: string
}

interface UiDialogQueueItem {
	state: UiDialogState
	resolve: (value: boolean) => void
}

@Injectable({
	providedIn: 'root'
})
export class UiDialogService {
	private dialogSubject = new BehaviorSubject<UiDialogState | null>(null)
	readonly dialog$ = this.dialogSubject.asObservable()

	private queue: UiDialogQueueItem[] = []
	private activeDialog: UiDialogQueueItem | null = null

	openAlert(message: string, options: UiAlertOptions = {}): Promise<void> {
		return new Promise(resolve => {
			this.enqueue({
				state: {
					mode: 'alert',
					title: options.title ?? 'DIALOG_ALERT_TITLE',
					message,
					primaryLabel: options.primaryLabel ?? 'ACCEPT',
					showCloseButton: options.showCloseButton ?? true,
					dismissableMask: options.dismissableMask ?? true,
					closeOnEscape: options.closeOnEscape ?? true
				},
				resolve: () => resolve()
			})
		})
	}

	openConfirm(message: string, options: UiConfirmOptions = {}): Promise<boolean> {
		return new Promise(resolve => {
			this.enqueue({
				state: {
					mode: 'confirm',
					title: options.title ?? 'DIALOG_CONFIRM_TITLE',
					message,
					primaryLabel: options.primaryLabel ?? 'ACCEPT',
					secondaryLabel: options.secondaryLabel ?? 'CANCEL',
					showCloseButton: options.showCloseButton ?? true,
					dismissableMask: options.dismissableMask ?? false,
					closeOnEscape: options.closeOnEscape ?? false
				},
				resolve
			})
		})
	}

	accept() {
		this.resolveActive(true)
	}

	reject() {
		this.resolveActive(false)
	}

	dismiss() {
		if (!this.activeDialog) {
			return
		}

		const fallbackResult = this.activeDialog.state.mode === 'confirm' ? false : true
		this.resolveActive(fallbackResult)
	}

	private enqueue(item: UiDialogQueueItem) {
		this.queue.push(item)
		this.openNextIfIdle()
	}

	private openNextIfIdle() {
		if (this.activeDialog || this.queue.length === 0) {
			return
		}

		this.activeDialog = this.queue.shift() ?? null
		this.dialogSubject.next(this.activeDialog?.state ?? null)
	}

	private resolveActive(value: boolean) {
		if (!this.activeDialog) {
			return
		}

		const currentDialog = this.activeDialog
		this.activeDialog = null
		this.dialogSubject.next(null)
		currentDialog.resolve(value)
		this.openNextIfIdle()
	}
}
